import { Box, Typography, Container, TextField, Button, Paper, List, ListItem, ListItemText, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Card, CardContent, CardHeader, CardActions, IconButton, CircularProgress, Grid, InputAdornment, keyframes, Pagination, AppBar, Toolbar, Link, Menu, MenuItem } from "@mui/material";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link as RouterLink, Navigate } from "react-router-dom";
import { useAuthService } from '../services/authService';
import { useUserData } from '../services/userDataService';
import backdrop from "../img/backdrop.png";
import forumService from '../services/forumService';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import ForumIcon from '@mui/icons-material/Forum';
import SortIcon from '@mui/icons-material/Sort';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { fetchAuthSession } from 'aws-amplify/auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme, useMediaQuery } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import adImage from "../img/ad-001.png";
import devices from "../img/devices.png";
import iosBadge from "../img/ios-badge.svg";
import googlePlayBadge from "../img/googleplay-badge.png";
import ReviewCards from '../components/ReviewCards';
import ReviewDetailDialog from '../components/ReviewDetailDialog';
import analyticsService from "../services/analyticsService.js";

// Define keyframes for button animation
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(255, 0, 0, 0.4);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
`;

// Top Banner Ad Component using devices image
const TopBanner = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleAdClick = () => {
    // Track ad click
    analyticsService.trackEvent('ad_click', {
      ad_name: 'top_banner_devices',
      page: 'reviews',
      position: 'top'
    });
    
    navigate('/subscribe');
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 4, 
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid rgba(255, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 20px rgba(255, 0, 0, 0.3)',
        },
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
      onClick={handleAdClick}
    >
      <Grid container sx={{ alignItems: 'center' }}>
        <Grid item xs={12} md={8}>
          <Box sx={{ padding: { xs: '20px 20px 10px 20px', md: '30px 20px 20px 30px' } }}>
            <Box 
              component="img"
              src={devices}
              alt="Screamify on all devices"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ 
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            textAlign: { xs: 'center', md: 'left' }
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 1, 
                fontSize: { xs: '1.4rem', sm: '1.6rem' } 
              }}
            >
              Watch Anywhere
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 1
              }}
            >
              Upgrade now for access to our hand picked movies, originals & podcasts.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mb: 2,
                fontStyle: 'italic'
              }}
            >
              We're starting small but we plan to be everywhere!
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                bgcolor: '#ff0000', 
                '&:hover': { bgcolor: '#cc0000' },
                minWidth: { xs: '100%', sm: '150px' },
                alignSelf: { xs: 'center', md: 'flex-start' },
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(255, 0, 0, 0.3)',
                py: 1
              }}
            >
              Upgrade Now
            </Button>
            
            {/* App Store Badges */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mt: 2,
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: { xs: 'center', md: 'flex-start' }
            }}>
              <Box 
                component="img"
                src={iosBadge}
                alt="Download on the App Store"
                sx={{
                  height: { xs: '32px', sm: '36px' },
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
              <Box 
                component="img"
                src={googlePlayBadge}
                alt="Get it on Google Play"
                sx={{
                  height: { xs: '32px', sm: '36px' },
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Create Thread Button Component
const CreateThreadButton = ({ onClick }) => {
  const { isAuthenticated } = useAuthService();
  const navigate = useNavigate();
  
  const handleCreateClick = () => {
    if (isAuthenticated) {
      onClick();
    } else {
      navigate('/login?activeTab=0');
    }
  };
  
  return (
    <Button
      variant="contained"
      color="error"
      onClick={handleCreateClick}
      sx={{
        bgcolor: '#ff0000',
        '&:hover': {
          bgcolor: '#cc0000'
        },
        whiteSpace: 'nowrap',
        fontWeight: 500,
        fontSize: { xs: '0.8rem', sm: '0.9rem' },
        textTransform: 'none',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          bgcolor: '#990000',
          transform: 'scale(0.98)'
        }
      }}
    >
      {isAuthenticated ? 'Create Review' : 'Sign In to Review'}
    </Button>
  );
};

// Ad component that matches the content card style but is half the height
const AdComponent = ({ adIndex }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.7)', 
          color: 'white',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          height: { xs: 'auto', sm: '120px' },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
            borderColor: 'rgba(255, 0, 0, 0.5)',
          },
          backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(20, 20, 20, 0.7))',
        }}
        onClick={() => navigate('/subscribe')}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            px: { xs: 3, sm: 4 },
            py: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: { xs: 'center', sm: 'left' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 0.5, 
                fontSize: { xs: '1rem', sm: '1.25rem' } 
              }}
            >
              Upgrade to Premium
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                maxWidth: { sm: '600px' }
              }}
            >
              Unlock exclusive horror content, personalized recommendations, and ad-free browsing.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            sx={{ 
              bgcolor: '#ff0000', 
              '&:hover': { bgcolor: '#cc0000' },
              minWidth: { xs: '100%', sm: '120px' },
              mt: { xs: 2, sm: 0 },
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Subscribe Now
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};

// Submission Ad Component for capturing filmmaker emails
const SubmissionAdComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const AIRTABLE_API_KEY = import.meta.env.VITE_AIRTABLE_API_KEY;
  const AIRTABLE_BASE_ID = 'appEp9ztiYve51qqI';
  const AIRTABLE_TABLE_NAME = 'Table%201'; // Use the same table that works in LandingPage.jsx
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    if (!AIRTABLE_API_KEY) {
      console.error('Airtable API key is not configured');
      setSnackbar({
        open: true,
        message: 'Server configuration error. Please try again later.',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_NAME}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            records: [
              {
                fields: {
                  Email: email,
                  Assignee: 'Film Submission',
                  Date: new Date().toISOString().split('T')[0]
                }
              }
            ]
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Airtable Error:', data);
        setSnackbar({
          open: true,
          message: 'Failed to submit your email. Please try again.',
          severity: 'error'
        });
      } else {
        setEmail('');
        setSnackbar({
          open: true,
          message: 'Thanks for your interest! We\'ll be in touch soon.',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred. Please try again later.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.8)', 
          color: 'white',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          height: 'auto',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
            borderColor: 'rgba(255, 0, 0, 0.5)',
          },
          backgroundImage: 'linear-gradient(to right, rgba(20, 0, 0, 0.8), rgba(40, 10, 10, 0.7))',
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexDirection: 'column',
            justifyContent: 'center',
            px: { xs: 3, sm: 4 },
            py: 3,
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold',
              color: '#ff0000',
              mb: 1, 
              fontSize: { xs: '1.2rem', sm: '1.4rem' },
              textAlign: 'center'
            }}
          >
            Calling All Horror Filmmakers!
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              mb: 2,
              textAlign: 'center'
            }}
          >
            Screamify is now accepting traditional and AI-generated horror shorts for our platform launch later this year.
          </Typography>
          
          <Box 
            component="form" 
            onSubmit={handleSubmit}
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto'
            }}
          >
            <TextField
              placeholder="Enter your email for submission details"
              variant="outlined"
              size="small"
              type="email"
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                sx: {
                  height: '44px'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ff0000',
                  },
                },
                '& input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            />
            <Button 
              type="submit"
              variant="contained" 
              disabled={loading}
              sx={{ 
                bgcolor: '#ff0000', 
                '&:hover': { bgcolor: '#cc0000' },
                minWidth: { xs: '100%', sm: '180px' },
                height: '44px',
                textTransform: 'none',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Submitting...' : 'Submit Now'}
            </Button>
          </Box>
        </Box>
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 2000 }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    </Grid>
  );
};

// Ad Banner Component
const AdBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // This will be replaced with dynamic data from DynamoDB later
  const adData = {
    imageUrl: adImage,
    link: "https://wickedsalemscandles.com",
    altText: "Wicked Salem's Candle Company - Horror-themed candles",
    advertiser: "Wicked Salem's Candle Company"
  };

  const navigate = useNavigate();

  const handleAdClick = (adType) => {
    // Track ad click
    analyticsService.trackEvent('ad_click', {
      ad_name: adType,
      ad_url: adData.link,
      page: 'reviews',
      position: 'banner'
    });
    
    navigate('/subscribe');
  };

  return (
    <Grid item xs={12}>
      <Box
        component="a"
        href={adData.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleAdClick(adData.advertiser)}
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          height: { xs: '200px', sm: '300px', md: '400px' },
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(255, 0, 0, 0.3)',
          },
          cursor: 'pointer',
          border: '1px solid rgba(255, 0, 0, 0.3)',
        }}
      >
        <Box
          component="img"
          src={adData.imageUrl}
          alt={adData.altText}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.02)'
            }
          }}
        />
      </Box>
    </Grid>
  );
};

// Custom header with hamburger menu for Community page
const CommunityHeader = ({ onLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleProfileClick = () => {
    navigate('/profile');
    handleMenuClose();
  };
  
  const handleLogout = () => {
    onLogout();
    handleMenuClose();
  };
  
  return (
    <Box sx={{ flexGrow: 1, mb: 2 }}>
      <AppBar position="static" sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenuOpen}
            sx={{ color: '#ffffff' }}
          >
            <MenuIcon />
          </IconButton>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'black',
                color: 'white',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                },
              },
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <AccountCircleIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
              Profile
            </MenuItem>
            <Divider sx={{ bgcolor: 'rgba(255, 0, 0, 0.3)' }} />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, color: 'rgba(255, 0, 0, 0.7)' }} />
              Logout User
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

function Community() {
  const { isAuthenticated, getToken, user, getDisplayName, signOut } = useAuthService();
  const { userData } = useUserData();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDialogOpen, setThreadDialogOpen] = useState(false);
  const [createThreadDialogOpen, setCreateThreadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const reviewsPerPage = 8;
  
  // Add sort state
  const [sortMethod, setSortMethod] = useState('top'); // 'top' or 'new'
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  
  // Form states
  const [threadTitle, setThreadTitle] = useState('');
  const [threadContent, setThreadContent] = useState('');
  const [postContent, setPostContent] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [activePostId, setActivePostId] = useState(null);
  const [userName, setUserName] = useState('');
  
  // Add state for movie search functionality
  const [threadPoster, setThreadPoster] = useState('');
  const [movieSearchDialogOpen, setMovieSearchDialogOpen] = useState(false);
  const [movieSearchQuery, setMovieSearchQuery] = useState('');
  const [movieSearchResults, setMovieSearchResults] = useState([]);
  const [movieSearchLoading, setMovieSearchLoading] = useState(false);
  
  // TMDB API constants
  const TMDB_API_KEY = '2089db4b95439747f34c0374c8c8e254';
  const TMDB_API_TOKEN = 'eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiIyMDg5ZGI0Yjk1NDM5NzQ3ZjM0YzAzNzRjOGM4ZTI1NCIsIm5iZiI6MTc0MjQ4NTM4NC44MDYwMDAyLCJzdWIiOiI2N2RjMzc4ODhhZjQ1MmYzMGZlOWY4ODkiLCJzY29wZXMiOlsiYXBpX3JlYWQiXSwidmVyc2lvbiI6MX0.QW2np_x0f5S2mEHi_NMzB-eW8bbkmXiOfY9pH1F9hmI';
  const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p/w500';
  
  // Genre mapping for displaying genre names
  const GENRE_MAP = {
    27: 'Horror',
    53: 'Thriller',
    9648: 'Mystery',
    80: 'Crime',
    878: 'Sci-Fi',
    14: 'Fantasy'
  };
  
  // Add state to track user votes
  const [userVotes, setUserVotes] = useState({});
  
  // Function to check if user has voted on an item
  const getUserVoteStatus = useCallback((itemType, itemId) => {
    // Using consistent key format
    const key = `${itemType}-${itemId}`;
    return userVotes[key] || null;
  }, [userVotes]);
  
  // Function to generate consistent vote keys
  const getVoteKey = useCallback((itemType, itemId) => {
    return `${itemType}-${itemId}`;
  }, []);
  
  // Get token on component mount
  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        console.log("User not authenticated, cannot fetch token");
        return;
      }
      
      try {
        const authToken = await getToken();
        if (!authToken) {
          console.error("Failed to get authentication token");
          setError("Failed to get authentication token. Please try logging in again.");
          return;
        }
        
        setToken(authToken);
        console.log("Token fetched successfully", authToken ? authToken.substring(0, 20) + "..." : "No token");
        
        // Set the userName from the authenticated user if available
        if (user) {
          setUserName(user.username || user.email || 'Anonymous');
        }
      } catch (error) {
        console.error("Error fetching token:", error);
        setError("Failed to get authentication token. Please try logging in again.");
      }
    };
    
    fetchToken();
  }, [isAuthenticated, getToken, user]);
  
  // Fetch threads when component mounts
  useEffect(() => {
    const fetchThreads = async () => {
      try {
        setLoading(true);
        
        // Only attempt to fetch threads if we have a token
        if (!token) {
          console.log('Waiting for token before fetching threads...');
          return; // Exit early if token is not available yet
        }
        
        // Pass the token to getThreads
        const data = await forumService.getThreads(token);
        console.log('Thread data received:', data);
        
        if (data && data.length > 0) {
          // Debug thread usernames
          data.forEach(thread => {
            console.log(`Thread ${thread.threadId} - userName: ${thread.userName}, userId: ${thread.userId}`);
          });
        }
        
        setThreads(data);
      } catch (error) {
        console.error('Error fetching threads:', error);
        setError('Failed to load threads. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchThreads();
  }, [token]); // Only re-run this effect when the token changes
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";
    try {
      // Handle ISO strings with 'Z' indicating UTC
      const date = new Date(dateString);
      
      // Format options for displaying the date in user's local timezone
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'  // Shows timezone abbreviation (e.g., EST, PST)
      };
      
      // Return date in user's local timezone
      return date.toLocaleString(undefined, options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };
  
  // Handle page change for pagination
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when page changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Get paginated threads
  const sortedThreads = useMemo(() => {
    // Create a copy to avoid mutation
    const threadsCopy = [...threads];
    
    if (sortMethod === 'top') {
      // Sort by most upvotes
      return threadsCopy.sort((a, b) => {
        const aVotes = (a.upvotes || 0) - (a.downvotes || 0);
        const bVotes = (b.upvotes || 0) - (b.downvotes || 0);
        return bVotes - aVotes; // Descending order
      });
    } else {
      // Sort by newest
      return threadsCopy.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    }
  }, [threads, sortMethod]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedThreads.length / reviewsPerPage);
  
  // Menu handlers
  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortChange = (method) => {
    setSortMethod(method);
    handleSortMenuClose();
    // Reset to first page when changing sort
    setPage(1);
  };
  
  // Handle thread selection to view details
  const handleThreadClick = async (threadId) => {
    try {
      setLoading(true);
      
      // Exit early if no token is available
      if (!token) {
        console.error("Cannot fetch thread details: Token is required");
        setError("Authentication required to view thread details");
        return;
      }
      
      // Pass the token to getThreadDetails
      const threadData = await forumService.getThreadDetails(threadId, token);
      
      // Only process user votes if logged in
      if (isAuthenticated && user?.sub) {
        // Process thread details to extract user votes
        const votes = {};
        
        // Thread votes
        if (threadData.votes && threadData.votes[user.sub]) {
          votes[getVoteKey('thread', threadData.threadId)] = threadData.votes[user.sub];
        }
        
        // Post votes
        if (threadData.posts) {
          threadData.posts.forEach(post => {
            if (post.votes && post.votes[user.sub]) {
              votes[getVoteKey('post', post.postId)] = post.votes[user.sub];
            }
            
            // Comment votes
            if (post.comments) {
              post.comments.forEach(comment => {
                if (comment.votes && comment.votes[user.sub]) {
                  votes[getVoteKey('comment', comment.commentId)] = comment.votes[user.sub];
                }
              });
            }
          });
        }
        
        // Update user votes state
        setUserVotes(prev => ({...prev, ...votes}));
      }
      
      setSelectedThread(threadData);
      setThreadDialogOpen(true);
    } catch (error) {
      console.error("Error fetching thread:", error);
      setError("Failed to load thread details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Get username from authenticated user or use input
  const getUsername = () => {
    let username = 'Anonymous';
    
    // First check if userData is available and has userHandle (this preserves capitalization)
    if (userData && userData.userHandle) {
      username = userData.userHandle;
      console.log('Using userHandle from userData:', username);
    }
    else if (user) {
      // First, try to get the userHandle from userData service if it's available
      if (user.attributes && user.attributes.userHandle) {
        username = user.attributes.userHandle;
        console.log('Using userHandle from attributes:', username);
      } 
      // Then prioritize the custom display name from Cognito attributes
      else if (user.attributes && user.attributes['custom:displayName']) {
        username = user.attributes['custom:displayName'];
        console.log('Using displayName from attributes:', username);
      } else if (user.username) {
        username = user.username;
        console.log('Using username from user object:', username);
      } else if (user.email) {
        username = user.email;
        console.log('Using email as username:', username);
      } else {
        username = 'Anonymous';
        console.log('No username found, using Anonymous');
      }
    } else {
      username = userName || 'Anonymous';
      console.log('User not available, using userName state:', username);
    }
    
    // Don't transform the username case - return it exactly as retrieved
    console.log('Final username (getUsername function):', username);
    return username;
  };
  
  // Function to search for movies
  const searchMovies = async (query) => {
    if (!query.trim()) return;
    
    try {
      setMovieSearchLoading(true);
      
      // Use the discover endpoint with genre filtering for horror, thriller, and mystery
      const discoverResponse = await fetch(
        `https://api.themoviedb.org/3/discover/movie?api_key=${TMDB_API_KEY}&with_genres=27,53,9648&sort_by=popularity.desc&include_adult=false&page=1&with_text_query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${TMDB_API_TOKEN}`,
            'accept': 'application/json'
          }
        }
      );
      
      if (!discoverResponse.ok) {
        throw new Error('Movie search failed');
      }
      
      const discoverData = await discoverResponse.json();
      
      // Filter discover results to ensure they match our query text
      const filteredResults = discoverData.results.filter(movie => 
        movie.title.toLowerCase().includes(query.toLowerCase()) ||
        (movie.overview && movie.overview.toLowerCase().includes(query.toLowerCase()))
      );
      
      // If we don't have enough results from discover endpoint, 
      // do a regular search and filter for our genres
      if (filteredResults.length < 5) {
        // Regular search for the title
        const searchResponse = await fetch(
          `https://api.themoviedb.org/3/search/movie?query=${encodeURIComponent(query)}&include_adult=false&language=en-US&page=1`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${TMDB_API_TOKEN}`,
              'accept': 'application/json'
            }
          }
        );
        
        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          
          // Filter the search results to only include horror/thriller/mystery movies
          const genreFilteredResults = searchData.results.filter(movie => 
            movie.genre_ids && movie.genre_ids.some(id => [27, 53, 9648].includes(id))
          );
          
          // Combine both result sets, removing duplicates
          const combinedResults = [...filteredResults];
          
          genreFilteredResults.forEach(movie => {
            if (!combinedResults.some(m => m.id === movie.id)) {
              combinedResults.push(movie);
            }
          });
          
          setMovieSearchResults(combinedResults);
        } else {
          setMovieSearchResults(filteredResults);
        }
      } else {
        setMovieSearchResults(filteredResults);
      }
    } catch (error) {
      console.error('Error searching movies:', error);
      setError('Failed to search movies. Please try again.');
    } finally {
      setMovieSearchLoading(false);
    }
  };
  
  // Function to handle movie search input changes
  const handleMovieSearchChange = (e) => {
    setMovieSearchQuery(e.target.value);
  };
  
  // Function to handle movie search submit
  const handleMovieSearchSubmit = (e) => {
    e.preventDefault();
    searchMovies(movieSearchQuery);
  };
  
  // Function to select a movie poster
  const selectMoviePoster = (movie) => {
    if (movie.poster_path) {
      setThreadPoster(`${TMDB_IMAGE_BASE_URL}${movie.poster_path}`);
    } else {
      setThreadPoster(''); // No poster available
    }
    setMovieSearchDialogOpen(false);
    setCreateThreadDialogOpen(true);
  };
  
  // Function to open the thread creation flow
  const openCreateThreadFlow = () => {
    setThreadTitle('');
    setThreadContent('');
    setThreadPoster('');
    setMovieSearchQuery('');
    setMovieSearchResults([]);
    setMovieSearchDialogOpen(true);
    getPopularHorrorMovies(); // Load popular horror movies when dialog opens
  };
  
  // Function to get popular horror movies
  const getPopularHorrorMovies = async () => {
    try {
      setMovieSearchLoading(true);
      
      // Get popular horror, thriller and mystery movies
      const response = await fetch(
        `https://api.themoviedb.org/3/discover/movie?api_key=${TMDB_API_KEY}&with_genres=27,53,9648&sort_by=popularity.desc&include_adult=false&page=1`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${TMDB_API_TOKEN}`,
            'accept': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch popular horror movies');
      }
      
      const data = await response.json();
      setMovieSearchResults(data.results || []);
    } catch (error) {
      console.error('Error fetching popular horror movies:', error);
    } finally {
      setMovieSearchLoading(false);
    }
  };
  
  // Function to add a message to a thread
  const addMessage = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!postContent.trim() || !selectedThread) return;
    
    // Check if token is available
    if (!token) {
      console.error("Cannot add message: Token is required");
      setError("Authentication token is required to post messages");
      return;
    }
    
    try {
      setMessageLoading(true);
      
      const username = getUsername();
      console.log('Adding message with username:', username);
      
      // Create the post
      const newPost = await forumService.addMessageToThread(
        selectedThread.threadId,
        postContent,
        token,
        username
      );
      
      // Reset message input
      setPostContent('');
      
      // Update thread locally without full refresh
      setSelectedThread(prevThread => ({
          ...prevThread,
        posts: [...(prevThread.posts || []), newPost]
      }));
      
    } catch (error) {
      console.error('Error adding post:', error);
      setError("Failed to add post. Please try again later.");
    } finally {
      setMessageLoading(false);
    }
  };
  
  // Create a new thread
  const handleCreateThread = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!threadTitle.trim() || !threadContent.trim()) {
      setError("Title and content are required");
      return;
    }
    
    // Check if token is available
    if (!token) {
      console.error("Cannot create thread: Token is required");
      setError("Authentication token is required to create threads");
      return;
    }
    
    setLoading(true);
    
    // Debug user information
    if (user) {
      console.log('User object:', user);
      if (user.attributes) {
        console.log('User attributes:', user.attributes);
        console.log('Display name:', user.attributes['custom:displayName']);
      }
    }
    
    // Use the getUsername function to get the username
    const username = getUsername();
    console.log('Creating thread with username:', username);
    
    try {
      // Pass the username directly to ensure it's used in the API call
      const newThread = await forumService.createThread(threadTitle, threadContent, token, threadPoster, username);
      
      // Instead of refetching all threads, just add the new thread to the existing array
      // This ensures the threadPoster is preserved
      setThreads(prevThreads => [newThread, ...prevThreads]);
      
      // Reset form
      setThreadTitle('');
      setThreadContent('');
      setThreadPoster('');
      setCreateThreadDialogOpen(false);
      
    } catch (error) {
      console.error("Error creating thread:", error);
      setError("Failed to create thread. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle voting on content
  const handleVote = async (itemId, itemType, voteType, event) => {
    // Stop event propagation to prevent thread selection when clicking vote buttons
    if (event) {
      event.stopPropagation();
    }
    
    // Require login to vote
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if token is available
    if (!token) {
      console.error("Cannot vote: Token is required");
      setError("Authentication token is required to vote");
      return;
    }
    
    try {
      // Update UI optimistically first
      const voteKey = getVoteKey(itemType, itemId);
      const currentVote = userVotes[voteKey];
      
      // Determine new vote state
      let newVote = voteType;
      if (currentVote === voteType) {
        // If clicking the same button, remove the vote
        newVote = null;
      }
      
      // Update vote counts in UI immediately
      if (itemType === 'thread') {
        // Find and update the thread in the threads list
        setThreads(prevThreads => 
          prevThreads.map(thread => {
            if (thread.threadId === itemId) {
              const upDelta = calculateVoteDelta('up', currentVote, newVote);
              const downDelta = calculateVoteDelta('down', currentVote, newVote);
              
              return {
                ...thread, 
                upvotes: (thread.upvotes || 0) + upDelta,
                downvotes: (thread.downvotes || 0) + downDelta
              };
            }
            return thread;
          })
        );
        
        // Also update the selected thread if it's the same one
        if (selectedThread && selectedThread.threadId === itemId) {
          setSelectedThread(prev => {
            const upDelta = calculateVoteDelta('up', currentVote, newVote);
            const downDelta = calculateVoteDelta('down', currentVote, newVote);
            
            return {
              ...prev, 
              upvotes: (prev.upvotes || 0) + upDelta,
              downvotes: (prev.downvotes || 0) + downDelta
            };
          });
        }
      } 
      else if (itemType === 'post' && selectedThread) {
        // Update post in the selected thread
        setSelectedThread(prev => ({
          ...prev,
          posts: prev.posts.map(post => {
            if (post.postId === itemId) {
              const upDelta = calculateVoteDelta('up', currentVote, newVote);
              const downDelta = calculateVoteDelta('down', currentVote, newVote);
              
              return {
                ...post, 
                upvotes: (post.upvotes || 0) + upDelta,
                downvotes: (post.downvotes || 0) + downDelta
              };
            }
            return post;
          })
        }));
      }
      else if (itemType === 'comment' && selectedThread) {
        // Update comment in the selected thread
        setSelectedThread(prev => ({
          ...prev,
          posts: prev.posts.map(post => {
            if (!post.comments) return post;
            
            const updatedComments = post.comments.map(comment => {
              if (comment.commentId === itemId) {
                const upDelta = calculateVoteDelta('up', currentVote, newVote);
                const downDelta = calculateVoteDelta('down', currentVote, newVote);
                
                return {
                  ...comment, 
                  upvotes: (comment.upvotes || 0) + upDelta,
                  downvotes: (comment.downvotes || 0) + downDelta
                };
              }
              return comment;
            });
            
            return {
              ...post,
              comments: updatedComments
            };
          })
        }));
      }
      
      // Update user vote state optimistically
      if (newVote) {
        setUserVotes(prev => ({
          ...prev,
          [voteKey]: newVote
        }));
      } else {
        setUserVotes(prev => {
          const newVotes = {...prev};
          delete newVotes[voteKey];
          return newVotes;
        });
      }
      
      // Make API call to record vote on server
      forumService.vote(itemId, itemType, newVote, token)
        .then(voteResult => {
          console.log("Vote result:", voteResult);
          
          if (voteResult.success) {
            // If we removed a vote, also remove it from our user votes state
            if (!newVote) {
              setUserVotes(prev => {
                const newVotes = {...prev};
                delete newVotes[voteKey];
                return newVotes;
              });
            }
            
            // Update UI based on item type with server values
            if (itemType === 'thread') {
              setThreads(prevThreads => 
                prevThreads.map(thread => 
                  thread.threadId === itemId ? {...thread, ...voteResult.item} : thread
                )
              );
              
              if (selectedThread && selectedThread.threadId === itemId) {
                setSelectedThread(prev => ({...prev, ...voteResult.item}));
              }
            } 
            else if (itemType === 'post' && selectedThread) {
              setSelectedThread(prev => ({
                ...prev,
                posts: prev.posts.map(post => 
                  post.postId === itemId ? {...post, ...voteResult.item} : post
                )
              }));
            }
            else if (itemType === 'comment' && selectedThread) {
              setSelectedThread(prev => ({
                ...prev,
                posts: prev.posts.map(post => {
                  if (!post.comments) return post;
                  
                  return {
                    ...post,
                    comments: post.comments.map(comment => 
                      comment.commentId === itemId ? {...comment, ...voteResult.item} : comment
                    )
                  };
                })
              }));
            }
          }
        })
        .catch(error => {
          console.error("Error voting:", error);
          // Revert optimistic update on error
          // This would need to restore the previous state
        });
      
    } catch (error) {
      console.error("Error voting:", error);
      setError("Failed to register vote. Please try again later.");
    }
  };
  
  // Helper function to calculate vote delta
  const calculateVoteDelta = (voteDirection, oldVote, newVote) => {
    if (oldVote === voteDirection && newVote !== voteDirection) {
      return -1; // Removing a vote
    } else if (oldVote !== voteDirection && newVote === voteDirection) {
      return 1;  // Adding a vote
    } else if (oldVote === (voteDirection === 'up' ? 'down' : 'up') && newVote === voteDirection) {
      return 2;  // Switching from opposite vote (counts as +2 for this direction)
    }
    return 0;  // No change
  };

  // Function to open thread detail dialog
  const openThreadDetailDialog = (thread) => {
    if (thread && thread.threadId) {
      handleThreadClick(thread.threadId);
    }
  };

  // State for message handling
  const [messageLoading, setMessageLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      // Force refresh of the page to ensure state is cleared
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#000000',
        backgroundImage: `url(${backdrop})`,
        backgroundSize: '150%',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Box sx={{ flex: 1 }}>
        <Header />
        
        {/* Toolbar for spacing below fixed AppBar */}
        <Toolbar />
        
        {/* Main content wrapper */}
        <Container maxWidth="xl" sx={{ 
          py: 4, 
          position: 'relative',
          zIndex: 1
        }}>
          {/* Centered Page Title */}
          <Typography variant="h4" sx={{ 
            color: '#ff0000', 
            fontWeight: 700,
            fontSize: { xs: '1.8rem', sm: '2.5rem' },
            textShadow: '0 0 10px rgba(255, 0, 0, 0.3)',
            textAlign: 'center',
            mb: 4
          }}>
            Community Discussion
          </Typography>
          
          {/* Top Banner Ad */}
          <TopBanner />
          
          {/* Error message */}
          {error && (
            <Typography 
              color="error" 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                p: 2, 
                borderRadius: 1,
                textAlign: 'center',
                mb: 3
              }}
            >
              {error}
            </Typography>
          )}
          
          {/* Sort and Create Controls - more subtle layout */}
          <Box sx={{ 
            display: 'none', /* Hide this entire controls section */
            justifyContent: 'flex-end',
            alignItems: 'center', 
            mb: 3
          }}>
            <Button
              variant="outlined"
              onClick={handleSortMenuOpen}
              startIcon={<SortIcon />}
              size="small"
              sx={{
                color: '#ffffff',
                borderColor: 'rgba(255, 0, 0, 0.7)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                  borderColor: '#ff0000'
                },
                whiteSpace: 'nowrap',
                mr: 2
              }}
            >
              {sortMethod === 'top' ? 'Top Reviews' : 'New Reviews'}
            </Button>
            
            <CreateThreadButton onClick={openCreateThreadFlow} />
          </Box>
          
          {/* Main Content */}
          <Box>
            {/* Review Cards Component */}
            <ReviewCards
              threads={sortedThreads}
              loading={loading}
              error={error}
              onOpenThread={openThreadDetailDialog}
              onVote={handleVote}
              onCreateReview={openCreateThreadFlow}
              getUserVoteStatus={getUserVoteStatus}
              canCreate={true}
              title=""
              emptyMessage="No reviews yet"
              emptyDescription="Be the first to share your horror movie experience!"
              sortMethod={sortMethod}
              onSortChange={handleSortChange}
              showAds={true}
              itemsPerPage={8}
              onPageChange={handlePageChange}
            />
          </Box>
          
          {/* ReviewDetailDialog Component */}
          <ReviewDetailDialog
            open={threadDialogOpen}
            onClose={() => setThreadDialogOpen(false)}
            selectedThread={selectedThread}
            comments={selectedThread?.posts || []}
            isLoadingComments={loading}
            newComment={postContent}
            setNewComment={setPostContent}
            onAddComment={addMessage}
            onVote={handleVote}
            getUserVoteStatus={getUserVoteStatus}
            isAuthenticated={isAuthenticated}
          />
        
        {/* Movie Search Dialog */}
        <Dialog
          open={movieSearchDialogOpen}
          onClose={() => setMovieSearchDialogOpen(false)}
          fullWidth
          maxWidth="lg"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              border: isMobile ? 'none' : '1px solid rgba(255, 0, 0, 0.3)',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
              height: isMobile ? '100%' : 'auto',
              margin: isMobile ? 0 : undefined,
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          TransitionProps={{
            onEnter: () => {
              if (isMobile) {
                // Prevent background scrolling on mobile
                document.body.style.overflow = 'hidden';
              }
            },
            onExited: () => {
              if (isMobile) {
                // Re-enable scrolling when dialog closes
                document.body.style.overflow = 'auto';
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            color: 'white', 
            borderBottom: '1px solid rgba(255, 0, 0, 0.3)', 
            pb: 2,
            position: isMobile ? 'sticky' : 'relative',
            top: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 1100 : 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.95)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ImageIcon sx={{ mr: 1, color: '#ff0000' }} /> 
              <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                Create a Horror Movie Review
              </Typography>
            </Box>
            <IconButton
              onClick={() => setMovieSearchDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            pt: 3,
            overflow: 'auto',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 0, 0, 0.6)',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)'
            }
          }}>
            <Typography variant="subtitle1" color="rgba(255, 255, 255, 0.7)" gutterBottom sx={{ fontStyle: 'italic' }}>
              Select a horror, thriller, or mystery movie to review
            </Typography>
            <form onSubmit={handleMovieSearchSubmit}>
              <TextField
                fullWidth
                margin="normal"
                placeholder="Enter movie title..."
                value={movieSearchQuery}
                onChange={handleMovieSearchChange}
                variant="outlined"
                InputProps={{
                  sx: { color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, py: 0.5 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton 
                        edge="end" 
                        type="submit"
                        size="large"
                        sx={{ color: '#ff0000' }}
                      >
                        <SearchIcon fontSize={isMobile ? 'medium' : 'large'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 0, 0, 0.5)',
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 0, 0, 0.8)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#ff0000',
                    },
                  },
                }}
              />
            </form>
            
            {movieSearchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexGrow: 1, alignItems: 'center' }}>
                <CircularProgress sx={{ color: '#ff0000' }} size={60} />
              </Box>
            ) : movieSearchResults.length > 0 ? (
              <Grid container spacing={2} sx={{ mt: 2, pb: isMobile ? 2 : 0 }}>
                {movieSearchResults.map(movie => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={movie.id}>
                    <Card 
                      sx={{ 
                        bgcolor: 'rgba(30, 30, 30, 0.9)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(60, 60, 60, 0.9)',
                          transform: 'translateY(-5px)',
                          boxShadow: '0 5px 15px rgba(255, 0, 0, 0.4)'
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                          boxShadow: '0 2px 8px rgba(255, 0, 0, 0.6)'
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255, 0, 0, 0.2)',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                      onClick={() => selectMoviePoster(movie)}
                    >
                      {movie.poster_path ? (
                        <Box
                          component="img"
                          src={`${TMDB_IMAGE_BASE_URL}${movie.poster_path}`}
                          alt={movie.title}
                          sx={{ 
                            width: '100%',
                            aspectRatio: '2/3',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '2/3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0, 0, 0, 0.6)'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: 'rgba(255, 255, 255, 0.3)' }} />
                        </Box>
                      )}
                      <CardContent sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
                        <Typography variant="h6" sx={{ color: 'white', fontSize: { xs: '0.8rem', sm: '0.9rem' }, mb: 1, fontWeight: 'bold' }}>
                          {movie.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          {movie.release_date ? movie.release_date.substring(0, 4) : 'Unknown year'}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {movie.genre_ids.slice(0, isMobile ? 2 : undefined).map(genreId => 
                            GENRE_MAP[genreId] && (
                              <Chip 
                                key={genreId}
                                label={GENRE_MAP[genreId]} 
                                size="small"
                                sx={{
                                  backgroundColor: genreId === 27 ? 'rgba(255, 0, 0, 0.7)' : // Horror
                                                 genreId === 53 ? 'rgba(255, 165, 0, 0.7)' : // Thriller
                                                 genreId === 9648 ? 'rgba(128, 0, 128, 0.7)' : // Mystery
                                                 'rgba(100, 100, 100, 0.7)', 
                                  color: 'white',
                                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                  height: { xs: '18px', sm: '20px' }
                                }}
                              />
                            )
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : movieSearchQuery.trim() !== '' ? (
              <Typography color="white" align="center" sx={{ p: 4, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                No movies found. Try a different search term.
              </Typography>
            ) : (
              <Typography color="white" align="center" sx={{ p: 2, mb: 2 }}>
                Showing popular horror, thriller, and mystery movies. Use the search box to find specific films.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: '1px solid rgba(255, 0, 0, 0.3)', 
            p: 2,
            position: isMobile ? 'sticky' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 1000
          }}>
            <Button
              onClick={() => {
                setMovieSearchDialogOpen(false);
                setCreateThreadDialogOpen(true);
              }}
              variant="outlined"
              fullWidth={isMobile}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255, 0, 0, 0.5)',
                '&:hover': {
                  borderColor: '#ff0000',
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                },
                py: isMobile ? 1 : undefined
              }}
            >
              Skip Selection
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Create Thread Dialog */}
        <Dialog
          open={createThreadDialogOpen}
          onClose={() => setCreateThreadDialogOpen(false)}
          fullWidth
          maxWidth="md"
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(20, 20, 20, 0.95)',
              border: isMobile ? 'none' : '1px solid rgba(255, 0, 0, 0.3)',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
              height: isMobile ? '100%' : 'auto',
              margin: isMobile ? 0 : undefined,
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          TransitionProps={{
            onEnter: () => {
              if (isMobile) {
                // Prevent background scrolling on mobile
                document.body.style.overflow = 'hidden';
              }
            },
            onExited: () => {
              if (isMobile) {
                // Re-enable scrolling when dialog closes
                document.body.style.overflow = 'auto';
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            color: 'white', 
            borderBottom: '1px solid rgba(255, 0, 0, 0.3)', 
            pb: 1,
            position: isMobile ? 'sticky' : 'relative',
            top: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 1100 : 'auto',
            backgroundColor: 'rgba(20, 20, 20, 0.95)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ForumIcon sx={{ mr: 1, color: '#ff0000' }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                Create Movie Review
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCreateThreadDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            pt: 3,
            overflow: 'auto',
            flexGrow: 1,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 0, 0, 0.6)',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)'
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, mb: 2 }}>
              <Box sx={{ 
                flex: { xs: '1', md: '0 0 30%' }, 
                mb: { xs: 3, md: 0 }, 
                mr: { md: 4 }, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                {threadPoster ? (
                  <Box sx={{ 
                    width: { xs: '120px', sm: '160px' }, 
                    height: { xs: '180px', sm: '240px' },
                    mb: 2,
                    flexShrink: 0
                  }}>
                    <Box
                      component="img"
                      src={threadPoster}
                      alt="Selected movie poster"
                      sx={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 1,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)'
                      }}
                    />
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      width: { xs: '120px', sm: '160px' }, 
                      height: { xs: '180px', sm: '240px' }, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'rgba(30, 30, 30, 0.8)',
                      borderRadius: 1,
                      mb: 2,
                      flexShrink: 0
                    }}
                  >
                    <ImageIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: 'rgba(255, 255, 255, 0.3)' }} />
                  </Box>
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    setCreateThreadDialogOpen(false);
                    setMovieSearchDialogOpen(true);
                  }}
                  sx={{ 
                    color: 'white', 
                    bgcolor: 'rgba(255, 0, 0, 0.7)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 0, 0, 0.9)',
                    },
                    width: { xs: '180px', sm: 'auto' }
                  }}
                  startIcon={<ImageIcon />}
                >
                  {threadPoster ? 'Change Poster' : 'Select Movie Poster'}
                </Button>
              </Box>
              
              <Box sx={{ flex: '1' }}>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      ml: 1,
                      mb: 0.5,
                      display: 'block'
                    }}
                  >
                    Posting as
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(60, 60, 60, 0.5)',
                      color: 'white',
                      border: '1px solid rgba(255, 0, 0, 0.5)',
                      borderRadius: 1,
                      fontWeight: 'medium',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        mr: 0.5
                      }}
                    >
                      @
                    </Typography>
                    <Typography 
                      component="span"
                      style={{ textTransform: 'none', letterSpacing: 'normal' }}
                      sx={{
                        textTransform: 'none !important', 
                        fontStyle: 'normal !important',
                        fontWeight: 'inherit !important',
                        fontSize: 'inherit !important',
                        letterSpacing: 'normal !important',
                        display: 'inline !important'
                      }}
                    >
                      {/* Force the username to retain its case */}
                      {String(getUsername())}
                    </Typography>
                  </Box>
                </Box>
                
                <TextField
                  fullWidth
                  label="Review Title (Put Name of Movie in Review)"
                  value={threadTitle}
                  onChange={(e) => setThreadTitle(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  InputProps={{
                    sx: { 
                      color: 'white',
                      backgroundColor: 'rgba(60, 60, 60, 0.5)' 
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 0, 0, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 0, 0, 0.8)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff0000',
                      },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Movie Review"
                  value={threadContent}
                  onChange={(e) => setThreadContent(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  required
                  multiline
                  rows={4}
                  InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                  InputProps={{
                    sx: { 
                      color: 'white',
                      backgroundColor: 'rgba(60, 60, 60, 0.5)' 
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 0, 0, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 0, 0, 0.8)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff0000',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            borderTop: '1px solid rgba(255, 0, 0, 0.3)', 
            p: 2,
            position: isMobile ? 'sticky' : 'relative',
            bottom: isMobile ? 0 : 'auto',
            backgroundColor: 'rgba(20, 20, 20, 0.95)',
            zIndex: 1000
          }}>
            <Button
              onClick={() => setCreateThreadDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateThread}
              variant="contained"
              disabled={!threadTitle || !threadContent}
              sx={{ 
                bgcolor: 'rgba(255, 0, 0, 0.7)',
                '&:hover': {
                  bgcolor: 'rgba(255, 0, 0, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 10px rgba(255, 0, 0, 0.3)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 0, 0, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)'
                },
                transition: 'all 0.2s ease',
                animation: `${pulseAnimation} 1.5s ease-in-out`,
                animationDelay: '0.5s',
                animationIterationCount: 1
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Thread'}
            </Button>
          </DialogActions>
        </Dialog>
        </Container>
      </Box>
      
      {/* Add Footer */}
      <Footer />
    </Box>
  );
}

export default Community; 